import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Brain, LogOut, User, Users, MessageSquare, ChevronDown, Shield, Bot, MessagesSquare, FileText, Sparkles, Tag, Settings, BookOpen, Stethoscope, Sun, Moon, Zap, Flower2 } from 'lucide-react';
import NotificationBell from './NotificationBell';

export const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const adminMenuRef = useRef(null);

  useEffect(() => {
    if (!showAdminMenu) return;
    const handleClickOutside = (e) => {
      if (adminMenuRef.current && !adminMenuRef.current.contains(e.target)) {
        setShowAdminMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAdminMenu]);

  const isAdmin = user?.role === 'admin';
  const isSuperUser = user?.role === 'superuser';
  const isPro = isSuperUser || isAdmin;
  const isDoctor = user?.user_type === 'doctor';

  // Get theme icon config
  const getThemeIcon = () => {
    switch (theme) {
      case 'dark':
        return { icon: Moon, color: '#FFA500', bg: 'linear-gradient(135deg, #2A3441 0%, #1E2530 100%)', shadow: '0 2px 8px rgba(0, 0, 0, 0.3)' };
      case 'cyberpunk':
        return { icon: Zap, color: '#00FFFF', bg: 'linear-gradient(135deg, #FF00FF 0%, #00FFFF 100%)', shadow: '0 0 15px rgba(255, 0, 255, 0.5)' };
      case 'floral':
        return { icon: Flower2, color: '#FFFFFF', bg: 'linear-gradient(135deg, #996515 0%, #7A5410 100%)', shadow: '0 4px 15px rgba(153, 101, 21, 0.5)' };
      case 'anime':
        return { icon: Sparkles, color: '#FFFFFF', bg: 'linear-gradient(135deg, #A03028 0%, #8B2520 100%)', shadow: '0 4px 15px rgba(160, 48, 40, 0.5)' };
      default:
        return { icon: Sun, color: '#FFFFFF', bg: 'linear-gradient(135deg, #FFE066 0%, #FFA500 100%)', shadow: '0 2px 8px rgba(255, 165, 0, 0.3)' };
    }
  };

  const themeConfig = getThemeIcon();
  const ThemeIcon = themeConfig.icon;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Determine home link based on user type
  const homeLink = isAuthenticated 
    ? (isDoctor ? "/doctor" : "/dashboard")
    : "/";

  return (
    <nav className="navbar-sunrise sticky top-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to={homeLink} className="flex items-center" data-testid="navbar-logo">
            <img 
              src="/logo-64.png" 
              alt="HearMeOut Logo" 
              className="h-12 object-contain -mr-1"
            />
            <span className="text-2xl font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>HearMeOut</span>
          </Link>

          {isAuthenticated && (
            <div className="flex items-center space-x-2">
              {/* Doctor Portal Link (for doctors only) */}
              {isDoctor && (
                <Link
                  to="/doctor"
                  className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-sm transition-colors hover:bg-cyan-500/20"
                  style={{ color: '#06B6D4' }}
                  data-testid="navbar-doctor-link"
                >
                  <Stethoscope className="w-4 h-4" />
                  <span className="hidden sm:inline">Patients</span>
                </Link>
              )}

              {/* Normal user navigation items - 5 tabs max */}
              {!isDoctor && (
                <>
                  {/* Forums Link */}
                  <Link
                    to="/forums"
                    className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-sm transition-colors"
                    style={{ background: 'rgba(34, 197, 94, 0.15)', color: '#22C55E' }}
                    data-testid="navbar-forums-link"
                  >
                    <MessagesSquare className="w-4 h-4" />
                    <span className="hidden sm:inline">Forums</span>
                  </Link>

                  {/* Group Therapy Link */}
                  <Link
                    to="/group-therapy"
                    className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-sm transition-colors"
                    style={{ background: 'rgba(236, 72, 153, 0.15)', color: '#EC4899' }}
                    data-testid="navbar-group-therapy-link"
                  >
                    <Users className="w-4 h-4" />
                    <span className="hidden sm:inline">Groups</span>
                  </Link>

                  {/* Content Library Link */}
                  <Link
                    to="/content"
                    className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-sm transition-colors"
                    style={{ background: 'rgba(6, 182, 212, 0.15)', color: '#06B6D4' }}
                    data-testid="navbar-content-link"
                  >
                    <BookOpen className="w-4 h-4" />
                    <span className="hidden sm:inline">Library</span>
                  </Link>

                  {/* Amigo Link */}
                  <Link
                    to="/amigo"
                    className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-sm transition-colors"
                    style={{ background: 'rgba(168, 85, 247, 0.15)', color: '#A855F7' }}
                    data-testid="navbar-amigo-link"
                  >
                    <Bot className="w-4 h-4" />
                    <span className="hidden sm:inline">Amigo</span>
                  </Link>
                </>
              )}

              {/* Admin Menu */}
              {isAdmin && (
                <div className="relative" ref={adminMenuRef}>
                  <button
                    onClick={() => setShowAdminMenu(!showAdminMenu)}
                    className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-sm transition-colors"
                    style={{ background: 'rgba(234, 179, 8, 0.15)', color: '#EAB308' }}
                    data-testid="admin-menu-button"
                  >
                    <Shield className="w-4 h-4" />
                    <span className="hidden sm:inline">Admin</span>
                    <ChevronDown className={`w-3 h-3 transition-transform ${showAdminMenu ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showAdminMenu && (
                    <div className="absolute right-0 mt-2 w-48 rounded-xl shadow-lg overflow-hidden z-50" style={{ background: '#FFF9E6', border: '1px solid rgba(255, 165, 0, 0.2)' }}>
                      <Link
                        to="/admin/users"
                        className="flex items-center space-x-2 px-4 py-3 transition-colors"
                        style={{ color: '#333333' }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 165, 0, 0.1)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        onClick={() => setShowAdminMenu(false)}
                        data-testid="admin-users-link"
                      >
                        <Users className="w-4 h-4" style={{ color: '#777777' }} />
                        <span>User Management</span>
                      </Link>
                      <Link
                        to="/admin/chats"
                        className="flex items-center space-x-2 px-4 py-3 transition-colors"
                        style={{ color: '#333333' }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 165, 0, 0.1)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        onClick={() => setShowAdminMenu(false)}
                        data-testid="admin-chats-link"
                      >
                        <MessageSquare className="w-4 h-4" style={{ color: '#777777' }} />
                        <span>User Chats</span>
                      </Link>
                      <Link
                        to="/admin/forum-moderation"
                        className="flex items-center space-x-2 px-4 py-3 transition-colors"
                        style={{ color: '#333333' }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 165, 0, 0.1)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        onClick={() => setShowAdminMenu(false)}
                        data-testid="admin-forum-moderation-link"
                      >
                        <FileText className="w-4 h-4" style={{ color: '#777777' }} />
                        <span>Forum Moderation</span>
                      </Link>
                      <Link
                        to="/admin/discount-codes"
                        className="flex items-center space-x-2 px-4 py-3 transition-colors"
                        style={{ color: '#333333' }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 165, 0, 0.1)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        onClick={() => setShowAdminMenu(false)}
                        data-testid="admin-discount-codes-link"
                      >
                        <Tag className="w-4 h-4" style={{ color: '#777777' }} />
                        <span>Discount Codes</span>
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {/* Profile Link with Pro Badge */}
              <Link
                to="/profile"
                className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full transition-colors relative text-sm"
                style={{ color: 'var(--text-dark)' }}
                data-testid="navbar-profile-link"
              >
                <div className="relative">
                  <User className="w-4 h-4" style={{ color: 'var(--text-light)' }} />
                  {isPro && (
                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full border-2" style={{ background: '#22C55E', borderColor: 'var(--card-bg)' }} title="Pro User"></span>
                  )}
                </div>
                <span className="hidden sm:inline">{user?.full_name?.split(' ')[0] || 'Profile'}</span>
                {isPro && (
                  <span className="hidden sm:flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-bold rounded-full" style={{ background: 'rgba(34, 197, 94, 0.15)', color: '#22C55E' }} data-testid="pro-badge">
                    <Sparkles className="w-2.5 h-2.5" />
                    Pro
                  </span>
                )}
              </Link>

              {/* Notification Bell */}
              <NotificationBell />

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full transition-all duration-300"
                style={{
                  background: themeConfig.bg,
                  boxShadow: themeConfig.shadow
                }}
                data-testid="theme-toggle-button"
                title="Switch Theme"
              >
                <ThemeIcon className="w-4 h-4" style={{ color: themeConfig.color }} />
              </button>

              {/* Settings Link */}
              <Link
                to="/settings"
                className="p-1.5 rounded-full transition-colors"
                data-testid="navbar-settings-link"
                title="Settings"
              >
                <Settings className="w-4 h-4" style={{ color: 'var(--text-light)' }} />
              </Link>

              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 rounded-full text-white transition-colors btn-primary-cta"
                data-testid="navbar-logout-button"
              >
                <LogOut className="w-5 h-5" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};